import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Download,
  Apple,
  Monitor,
  Terminal,
  Chrome,
  Smartphone,
  ArrowRight,
  ExternalLink,
  Command,
  Layout,
  Puzzle,
} from 'lucide-react';
import { clsx } from 'clsx';
import { isElectron, getClientOS, type ClientOS } from '@/utils/platform';
import { chromeExtensionInstallUrl } from '@/desktop/constants';
import { openExternalUrl } from '@/desktop/open-external';

type DesktopOS = 'macos' | 'windows' | 'linux';

type RecommendedDownload = {
  id: ClientOS;
  icon: React.ElementType;
  actionIcon?: React.ElementType;
  title: string;
  description: string;
  version?: string;
  updated?: string;
  primaryLabel: string;
  secondaryLabel?: string;
};

function SectionHeading({
  icon: Icon,
  title,
}: {
  icon: React.ElementType;
  title: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <h3 className="text-sm font-bold text-foreground">{title}</h3>
    </div>
  );
}

const primaryBtnClass =
  'inline-flex min-h-9 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90';

const ghostBtnClass =
  'inline-flex min-h-9 items-center justify-center gap-2 rounded-lg bg-muted/70 px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted';

function RecommendedDownloadCard({
  icon: Icon,
  actionIcon: ActionIcon = Download,
  title,
  description,
  version,
  updated,
  primaryLabel,
  secondaryLabel,
  badge,
}: RecommendedDownload & { badge: string }) {
  return (
    <div className="overflow-hidden rounded-2xl bg-card ring-1 ring-primary/25">
      <div className="bg-primary/5 px-5 py-4 sm:px-6">
        <span className="inline-flex rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">
          {badge}
        </span>
        <div className="mt-4 flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 space-y-1">
            <h3 className="text-base font-bold text-foreground">{title}</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
            {version ? (
              <p className="text-xs text-muted-foreground">
                {version}
                {updated ? <span> · {updated}</span> : null}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 border-t border-border-muted px-5 py-4 sm:px-6">
        <button type="button" className={primaryBtnClass}>
          <ActionIcon className="h-4 w-4" aria-hidden />
          {primaryLabel}
        </button>
        {secondaryLabel ? (
          <button type="button" className={ghostBtnClass}>
            {secondaryLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function PlatformRow({
  icon: Icon,
  name,
  subtitle,
  badge,
  children,
}: {
  icon: React.ElementType;
  name: string;
  subtitle: string;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:gap-4 sm:px-5">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground">
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-foreground">{name}</p>
            {badge ? (
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                {badge}
              </span>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">{children}</div>
    </div>
  );
}

function ExtensionRow({
  name,
  icon: Icon,
  cta,
  onClick,
}: {
  name: string;
  icon: React.ElementType;
  cta: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1 text-sm font-medium text-foreground">{name}</span>
      <span className="shrink-0 text-xs font-semibold text-primary">{cta}</span>
    </button>
  );
}

function useRecommendedDownload(): RecommendedDownload | null {
  const { t } = useTranslation('settings');
  const os = getClientOS();

  return useMemo(() => {
    switch (os) {
      case 'macos':
        return {
          id: 'macos',
          icon: Apple,
          title: t('downloads.macOS.title'),
          description: t('downloads.macOS.description'),
          version: t('downloads.macOS.version'),
          updated: t('downloads.macOS.updated'),
          primaryLabel: t('downloads.macOS.download'),
        };
      case 'windows':
        return {
          id: 'windows',
          icon: Layout,
          title: t('downloads.windows.title'),
          description: t('downloads.windows.description'),
          version: t('downloads.windows.version'),
          updated: t('downloads.windows.updated'),
          primaryLabel: t('downloads.windows.download'),
        };
      case 'linux':
        return {
          id: 'linux',
          icon: Terminal,
          title: t('downloads.linux.title'),
          description: t('downloads.linux.description'),
          version: t('downloads.linux.version'),
          updated: t('downloads.linux.updated'),
          primaryLabel: t('downloads.linux.deb'),
          secondaryLabel: t('downloads.linux.rpm'),
        };
      case 'ios':
        return {
          id: 'ios',
          icon: Apple,
          actionIcon: ArrowRight,
          title: t('downloads.ios.title'),
          description: t('downloads.ios.description'),
          primaryLabel: t('downloads.ios.download'),
        };
      case 'android':
        return {
          id: 'android',
          icon: Smartphone,
          actionIcon: ArrowRight,
          title: t('downloads.android.title'),
          description: t('downloads.android.description'),
          primaryLabel: t('downloads.android.download'),
        };
      default:
        return null;
    }
  }, [os, t]);
}

export const DownloadsSettings = () => {
  const { t } = useTranslation('settings');
  const onDesktop = isElectron();
  const clientOS = getClientOS();
  const recommended = useRecommendedDownload();

  const showDesktopPlatforms = !onDesktop && !['ios', 'android'].includes(clientOS);
  const hiddenDesktopOS: DesktopOS[] = recommended
    ? (['macos', 'windows', 'linux'] as const).filter((id) => id !== recommended.id)
    : ['macos', 'windows', 'linux'];

  return (
    <div className="max-w-2xl space-y-6 pb-8">
      <header className="space-y-1">
        <h2 className="text-2xl font-bold tracking-tight text-foreground">{t('downloads.title')}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{t('downloads.description')}</p>
      </header>

      {onDesktop ? (
        <section className="space-y-3">
          <SectionHeading icon={Puzzle} title={t('downloads.desktopCompanion.title')} />
          <div className="rounded-2xl bg-amber-500/5 p-5 ring-1 ring-amber-500/20 sm:p-6">
            <p className="text-sm leading-relaxed text-muted-foreground">{t('downloads.desktopCompanion.body')}</p>
            <button
              type="button"
              className={clsx(primaryBtnClass, 'mt-4')}
              onClick={() => void openExternalUrl(chromeExtensionInstallUrl('desktop_downloads'))}
            >
              {t('downloads.desktopCompanion.cta')}
            </button>
          </div>
        </section>
      ) : recommended ? (
        <RecommendedDownloadCard {...recommended} badge={t('downloads.recommendedForYou')} />
      ) : null}

      {showDesktopPlatforms ? (
        <section className="space-y-3">
          <SectionHeading icon={Monitor} title={t('downloads.otherPlatforms')} />
          <div className="divide-y divide-border-muted overflow-hidden rounded-2xl bg-card">
            {hiddenDesktopOS.includes('macos') ? (
              <PlatformRow name={t('downloads.macOS.title')} subtitle="macOS" icon={Apple}>
                <button type="button" className={ghostBtnClass}>
                  <Download className="h-3.5 w-3.5" aria-hidden />
                  {t('downloads.macOS.download')}
                </button>
              </PlatformRow>
            ) : null}
            {hiddenDesktopOS.includes('windows') ? (
              <PlatformRow
                name={t('downloads.windows.name')}
                subtitle={t('downloads.windows.os')}
                icon={Layout}
                badge="X64"
              >
                <button type="button" className={ghostBtnClass}>
                  <Download className="h-3.5 w-3.5" aria-hidden />
                  {t('downloads.windows.download')}
                </button>
              </PlatformRow>
            ) : null}
            {hiddenDesktopOS.includes('linux') ? (
              <PlatformRow
                name={t('downloads.linux.name')}
                subtitle={t('downloads.linux.os')}
                icon={Terminal}
                badge="DEB / RPM"
              >
                <button type="button" className={ghostBtnClass}>
                  {t('downloads.linux.deb')}
                </button>
                <button type="button" className={ghostBtnClass}>
                  {t('downloads.linux.rpm')}
                </button>
              </PlatformRow>
            ) : null}
            <PlatformRow
              name={t('downloads.cli.name')}
              subtitle={t('downloads.cli.os')}
              icon={Command}
              badge="CLI"
            >
              <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
                <code className="text-xs font-mono text-muted-foreground">npm i -g trovepile</code>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              </div>
            </PlatformRow>
          </div>
        </section>
      ) : null}

      <section className="space-y-3">
        <SectionHeading
          icon={Chrome}
          title={onDesktop ? t('downloads.desktopCompanion.extensionsHeading') : t('downloads.browserExtensions')}
        />
        <div className="space-y-0.5 rounded-2xl bg-card p-2">
          <ExtensionRow
            name="Chrome"
            icon={Chrome}
            cta={t('downloads.extensions.addToChrome')}
            onClick={() => void openExternalUrl(chromeExtensionInstallUrl('desktop_downloads'))}
          />
          <ExtensionRow name="Firefox" icon={Download} cta={t('downloads.extensions.addToFirefox')} />
          <ExtensionRow name="Edge" icon={Layout} cta={t('downloads.extensions.addToEdge')} />
          <ExtensionRow name="Safari" icon={Apple} cta={t('downloads.extensions.viewInAppStore')} />
        </div>
      </section>

      {!onDesktop && !['ios', 'android'].includes(clientOS) ? (
        <section className="space-y-3">
          <SectionHeading icon={Smartphone} title={t('downloads.mobileApps')} />
          <div className="divide-y divide-border-muted overflow-hidden rounded-2xl bg-card">
            <button
              type="button"
              className="flex w-full items-center gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/30 sm:px-5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-foreground text-background">
                <Apple className="h-4 w-4" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  {t('downloads.ios.badge')}
                </p>
                <p className="text-sm font-semibold text-foreground">{t('downloads.ios.store')}</p>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground/50" aria-hidden />
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/30 sm:px-5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-foreground text-background">
                <Smartphone className="h-4 w-4 text-primary" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  {t('downloads.android.badge')}
                </p>
                <p className="text-sm font-semibold text-foreground">{t('downloads.android.store')}</p>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground/50" aria-hidden />
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
};
